import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Send, Bot, User, MessageSquare, Plus, Search,
  Phone, Video, MoreVertical, Paperclip, Smile
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Avatar } from '../components/ui/avatar';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ChatPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = getUser();
  const messagesEndRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchConversations();
    
    const conversationId = searchParams.get('conversation');
    if (conversationId) {
      loadConversation(conversationId);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/conversations');
      setConversations(response.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل المحادثات' : 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      setLoading(true);
      const [convRes, messagesRes] = await Promise.all([
        api.get(`/conversations/${conversationId}`),
        api.get(`/chat/messages/${conversationId}`)
      ]);
      setActiveConversation(convRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل المحادثة' : 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    setSending(true);
    try {
      const response = await api.post('/chat/messages', {
        conversation_id: activeConversation.id,
        message: newMessage.trim()
      });
      
      setMessages([...messages, response.data]);
      setNewMessage('');
      
      // Update conversation in list
      setConversations(conversations.map(conv => 
        conv.id === activeConversation.id 
          ? { ...conv, last_message: newMessage.trim(), last_message_at: new Date() }
          : conv
      ));
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const startAIChat = async () => {
    try {
      // Create AI conversation
      const response = await api.post('/conversations', {
        conversation_type: 'client_ai',
        title: i18n.language === 'ar' ? 'استشارة مع المساعد القانوني الذكي' : 'Consultation with AI Legal Assistant'
      });
      
      await fetchConversations();
      await loadConversation(response.data.id);
      toast.success(i18n.language === 'ar' ? 'تم بدء محادثة مع المساعد الذكي' : 'AI chat started');
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل بدء المحادثة' : 'Failed to start chat');
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getConversationIcon = (conv) => {
    if (conv.conversation_type === 'client_ai') return <Bot className="w-5 h-5" />;
    return <User className="w-5 h-5" />;
  };

  const getMessageSenderName = (msg) => {
    if (msg.sender_role === 'ai') return i18n.language === 'ar' ? 'المساعد الذكي' : 'AI Assistant';
    if (msg.sender_id === user.id) return i18n.language === 'ar' ? 'أنت' : 'You';
    return i18n.language === 'ar' ? 'المحامي' : 'Lawyer';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <Card className="shadow-xl border-0 overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
            <div className="flex h-full">
              {/* Sidebar - Conversations List */}
              <div className="w-80 border-r bg-white flex flex-col">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between mb-4">
                    <CardTitle className="text-xl">
                      {i18n.language === 'ar' ? 'المحادثات' : 'Conversations'}
                    </CardTitle>
                    <Button size="sm" onClick={startAIChat} className="btn-primary">
                      <Plus className="w-4 h-4 ml-1" />
                      {i18n.language === 'ar' ? 'AI' : 'AI'}
                    </Button>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder={i18n.language === 'ar' ? 'بحث...' : 'Search...'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </CardHeader>
                
                <div className="flex-1 overflow-y-auto">
                  {loading && conversations.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="spinner" />
                    </div>
                  ) : filteredConversations.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">
                        {i18n.language === 'ar' ? 'لا توجد محادثات' : 'No conversations'}
                      </p>
                      <Button onClick={startAIChat} className="mt-4 btn-primary">
                        <Bot className="w-4 h-4 ml-2" />
                        {i18n.language === 'ar' ? 'ابدأ محادثة مع AI' : 'Start AI Chat'}
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredConversations.map((conv) => (
                        <div
                          key={conv.id}
                          onClick={() => loadConversation(conv.id)}
                          className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                            activeConversation?.id === conv.id ? 'bg-primary/5' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              conv.conversation_type === 'client_ai' 
                                ? 'bg-gradient-to-br from-purple-500 to-purple-600' 
                                : 'bg-gradient-to-br from-primary to-primary-light'
                            } text-white`}>
                              {getConversationIcon(conv)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="font-semibold text-sm truncate">{conv.title}</h4>
                                {conv.last_message_at && (
                                  <span className="text-xs text-gray-500">
                                    {format(new Date(conv.last_message_at), 'HH:mm')}
                                  </span>
                                )}
                              </div>
                              {conv.last_message && (
                                <p className="text-sm text-gray-600 truncate">{conv.last_message}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className="flex-1 flex flex-col bg-gray-50">
                {activeConversation ? (
                  <>
                    {/* Chat Header */}
                    <div className="bg-white border-b px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            activeConversation.conversation_type === 'client_ai'
                              ? 'bg-gradient-to-br from-purple-500 to-purple-600'
                              : 'bg-gradient-to-br from-primary to-primary-light'
                          } text-white`}>
                            {getConversationIcon(activeConversation)}
                          </div>
                          <div>
                            <h3 className="font-semibold">{activeConversation.title}</h3>
                            <Badge variant="outline" className="mt-1">
                              {activeConversation.conversation_type === 'client_ai' 
                                ? (i18n.language === 'ar' ? 'مساعد ذكي' : 'AI Assistant')
                                : (i18n.language === 'ar' ? 'محامي' : 'Lawyer')
                              }
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost">
                            <Phone className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Video className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                      {loading && messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="spinner" />
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="text-center py-12">
                          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-500">
                            {i18n.language === 'ar' ? 'ابدأ المحادثة' : 'Start the conversation'}
                          </p>
                        </div>
                      ) : (
                        messages.map((msg) => {
                          const isOwn = msg.sender_id === user.id;
                          const isAI = msg.sender_role === 'ai';
                          
                          return (
                            <div
                              key={msg.id}
                              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`flex gap-2 max-w-[70%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isAI 
                                    ? 'bg-gradient-to-br from-purple-500 to-purple-600'
                                    : isOwn
                                    ? 'bg-gradient-to-br from-primary to-primary-light'
                                    : 'bg-gradient-to-br from-gray-500 to-gray-600'
                                } text-white text-sm font-semibold`}>
                                  {isAI ? <Bot className="w-4 h-4" /> : getMessageSenderName(msg)[0]}
                                </div>
                                <div>
                                  <div className={`rounded-2xl px-4 py-2 ${
                                    isOwn
                                      ? 'bg-primary text-white'
                                      : isAI
                                      ? 'bg-purple-100 text-gray-900'
                                      : 'bg-white text-gray-900 border'
                                  }`}>
                                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                                  </div>
                                  <p className={`text-xs text-gray-500 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                                    {format(new Date(msg.created_at), 'HH:mm')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input */}
                    <div className="bg-white border-t px-6 py-4">
                      <form onSubmit={handleSendMessage} className="flex gap-3">
                        <Button type="button" size="sm" variant="ghost">
                          <Paperclip className="w-5 h-5" />
                        </Button>
                        <Button type="button" size="sm" variant="ghost">
                          <Smile className="w-5 h-5" />
                        </Button>
                        <Input
                          placeholder={i18n.language === 'ar' ? 'اكتب رسالة...' : 'Type a message...'}
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          className="flex-1"
                          disabled={sending}
                        />
                        <Button 
                          type="submit" 
                          className="btn-primary" 
                          disabled={!newMessage.trim() || sending}
                        >
                          <Send className="w-5 h-5" />
                        </Button>
                      </form>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">
                        {i18n.language === 'ar' ? 'اختر محادثة' : 'Select a conversation'}
                      </h3>
                      <p className="text-gray-500 mb-4">
                        {i18n.language === 'ar' 
                          ? 'اختر محادثة من القائمة أو ابدأ محادثة جديدة'
                          : 'Choose a conversation from the list or start a new one'
                        }
                      </p>
                      <Button onClick={startAIChat} className="btn-primary">
                        <Bot className="w-5 h-5 ml-2" />
                        {i18n.language === 'ar' ? 'محادثة مع AI' : 'Chat with AI'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ChatPage;
